/**
 * Where a signup started, when it started on a public preview page or a partner page.
 *
 * `/routepreview/:tripId` and `/listpreview/:listId` open the login modal from their CTA, and the
 * `user_signup` that follows is fired from RegisterBox with `location: "auth"` -- it carries nothing
 * about the page behind the modal. The CTA leaves a note here; the signup takes it. Held in
 * sessionStorage so it survives the popup/redirect sign-in within the tab, and dropped after an hour
 * so a signup much later in the visit is not credited to a preview page the visitor left long ago.
 */
export type SignupOriginKind = "routepreview" | "listpreview" | "madmonkey" | "countrymap";

const STORAGE_KEY = "stippl_signup_origin";
const MAX_AGE_MS = 60 * 60 * 1000;

export function setSignupOrigin(kind: SignupOriginKind, id: string): void {
	if (typeof window === "undefined") return;
	try {
		window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ kind, id, at: Date.now() }));
	} catch {
		// sessionStorage unavailable (private mode / blocked): the signup just goes unattributed
	}
}

/** Event properties for `user_signup`; one signup consumes the entry, a stale one is dropped. */
export function takeSignupOrigin(): { signup_origin?: string; origin_id?: string } {
	if (typeof window === "undefined") return {};
	try {
		const raw = window.sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return {};
		window.sessionStorage.removeItem(STORAGE_KEY);
		const { kind, id, at } = JSON.parse(raw);
		// `!(age < max)` rather than `age >= max`, so a missing or non-numeric `at` is stale too.
		if (typeof kind !== "string" || typeof id !== "string" || !(Date.now() - at < MAX_AGE_MS)) return {};
		return { signup_origin: kind, origin_id: id };
	} catch {
		return {}; // unreadable storage or garbage JSON
	}
}
